(() => {
  class ProductPackBuilder {
    constructor(element) {
      this.element = element;
      this.form = document.getElementById(element.dataset.formId);
      this.errorElement = element.querySelector('[data-pack-error]');
      this.components = this.parseComponents();

      this.onSubmit = this.onSubmit.bind(this);
      this.onChipClick = this.onChipClick.bind(this);

      this.element.addEventListener('click', this.onChipClick);
      if (this.form && this.components.length) {
        this.form.addEventListener('submit', this.onSubmit, true);
      }

      this.updateSelections();
    }

    parseComponents() {
      const script = this.element.querySelector('[data-pack-components]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent).map((component) => ({
          ...component,
          selectedVariant: null
        }));
      } catch (error) {
        console.error('Pack builder: invalid component data', error);
        return [];
      }
    }

    onChipClick(event) {
      const chip = event.target.closest('[data-pack-chip]');
      if (!chip || !this.element.contains(chip)) return;

      event.preventDefault();

      const select = this.element.querySelector(
        `[data-pack-option][data-component-index="${chip.dataset.componentIndex}"][data-option-index="${chip.dataset.optionIndex}"]`
      );

      if (!select) return;
      select.value = chip.dataset.value;
      this.updateSelections();
    }

    updateSelections() {
      this.components.forEach((component, index) => {
        const selectedOptions = Array.from(this.element.querySelectorAll(`[data-pack-option][data-component-index="${index}"]`))
          .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
          .map((select) => select.value);

        component.selectedVariant = this.findVariant(component, selectedOptions);
        this.updateOptionChips(index);
        this.updateOptionImages(component, index, selectedOptions);
      });
    }

    updateOptionChips(componentIndex) {
      this.element.querySelectorAll(`[data-pack-option][data-component-index="${componentIndex}"]`).forEach((select) => {
        const optionIndex = select.dataset.optionIndex;
        const current = select.closest('.product-customizer__variant-field')?.querySelector('[data-pack-current-value]');

        if (current) current.textContent = select.value;

        this.element.querySelectorAll(
          `[data-pack-chip][data-component-index="${componentIndex}"][data-option-index="${optionIndex}"]`
        ).forEach((chip) => {
          chip.classList.toggle('is-active', chip.dataset.value === select.value);
        });
      });
    }

    updateOptionImages(component, componentIndex, selectedOptions) {
      this.element.querySelectorAll(
        `[data-pack-chip][data-component-index="${componentIndex}"] img`
      ).forEach((image) => {
        const chip = image.closest('[data-pack-chip]');
        if (!chip) return;

        const optionIndex = Number(chip.dataset.optionIndex);
        const candidateOptions = [...selectedOptions];
        candidateOptions[optionIndex] = chip.dataset.value;
        const variant = this.findVariant(component, candidateOptions);
        const variantImage = variant?.image || component.featured_image;

        if (!variantImage?.src || image.getAttribute('src') === variantImage.src) return;

        image.setAttribute('src', variantImage.src);
        image.removeAttribute('srcset');
      });
    }

    findVariant(component, selectedOptions) {
      if (!selectedOptions.length) {
        return component.variants.find((variant) => variant.available) || component.variants[0];
      }

      return component.variants.find((variant) => {
        return variant.available && variant.options.every((option, index) => option === selectedOptions[index]);
      }) || component.variants.find((variant) => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    }

    async onSubmit(event) {
      if (!this.form || !this.components.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.updateSelections();
      const invalidComponent = this.components.find((component) => !component.selectedVariant || !component.selectedVariant.available);
      if (invalidComponent) {
        this.showError('Revisa las opciones del pack: hay una combinación no disponible.');
        return;
      }

      const missingField = this.getMissingRequiredField();
      if (missingField) {
        this.showError(`Completa el campo obligatorio: ${missingField.dataset.fieldLabel}.`);
        missingField.focus();
        return;
      }

      const submitter = event.submitter || this.form.querySelector('[type="submit"]');
      const quantity = this.getQuantity();
      const packVariantId = this.getPackVariantId();
      const sectionsToBundle = [];

      if (!packVariantId) {
        this.showError('No se ha podido identificar la variante del pack.');
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const properties = this.getPackProperties();
      const items = [{
        id: packVariantId,
        quantity,
        properties
      }];

      this.setLoading(submitter, true);
      this.showError(false);

      try {
        const config = window.theme?.utils?.fetchConfig ? window.theme.utils.fetchConfig('javascript') : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
        };

        config.headers = config.headers || {};
        config.headers['Content-Type'] = 'application/json';
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        const response = await fetch(window.theme.routes.cart_add_url, config);
        const parsedState = await response.json();

        if (parsedState.status) {
          this.showError(parsedState.description || parsedState.message || 'No se ha podido añadir el pack al carrito.');
          return;
        }

        const cartJson = await (await fetch(window.theme.routes.cart_url, { ...window.theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson.sections = parsedState.sections;

        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-pack-builder',
          cart: cartJson
        });

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: parsedState }
        }));

        document.querySelector('cart-drawer')?.show(submitter);
      } catch (error) {
        console.error(error);
        this.showError('No se ha podido añadir el pack al carrito.');
      } finally {
        this.setLoading(submitter, false);
      }
    }

    getQuantity() {
      const quantity = Number(new FormData(this.form).get('quantity') || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getPackVariantId() {
      const formDataId = new FormData(this.form).get('id');
      const inputId = this.form.querySelector('[name="id"]')?.value;
      return formDataId || inputId;
    }

    getMissingRequiredField() {
      return Array.from(this.element.querySelectorAll('[data-pack-field][data-required="true"]'))
        .find((field) => !field.value.trim());
    }

    getPackProperties() {
      return this.components.reduce((properties, component, index) => {
        const prefix = `Producto ${index + 1}`;

        properties[prefix] = component.title;

        const optionNames = Array.isArray(component.options) ? component.options : [];
        component.selectedVariant.options.forEach((value, optionIndex) => {
          const optionName = optionNames[optionIndex] || `Opción ${optionIndex + 1}`;
          properties[`${prefix} - ${optionName}`] = value;
        });

        Object.assign(properties, this.getComponentFieldProperties(index, prefix));

        return properties;
      }, {});
    }

    getComponentFieldProperties(componentIndex, prefix) {
      return Array.from(this.element.querySelectorAll(`[data-pack-field][data-component-index="${componentIndex}"]`))
        .reduce((properties, field) => {
          const value = field.value.trim();
          if (value) properties[`${prefix} - ${field.dataset.fieldLabel}`] = value;
          return properties;
        }, {});
    }

    setLoading(button, loading) {
      if (!button) return;
      button.toggleAttribute('aria-busy', loading);
      if (loading) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = !message;
      this.errorElement.textContent = message || '';
    }
  }

  const initPackBuilders = (root = document) => {
    root.querySelectorAll('[data-pack-builder]:not([data-pack-builder-ready])').forEach((element) => {
      element.setAttribute('data-pack-builder-ready', 'true');
      new ProductPackBuilder(element);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPackBuilders());
  } else {
    initPackBuilders();
  }

  document.addEventListener('shopify:section:load', (event) => initPackBuilders(event.target));
})();
